import { Directive, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';

/**
 * Adds mouse drag-to-scroll behavior to a horizontally scrollable container.
 * On desktop, users can click and drag to scroll the container sideways.
 *
 * Features:
 * - cursor: grab / grabbing visual feedback
 * - Suppresses click events on children when a drag occurred (prevents accidental chip selection)
 * - Works alongside native touch scroll on mobile
 *
 * Usage: <div appDragScroll class="overflow-x-auto">...</div>
 */
@Directive({
    selector: '[appDragScroll]',
    standalone: true,
})
export class DragScrollDirective implements OnInit, OnDestroy {
    private el: HTMLElement;
    private isDown = false;
    private wasDragged = false;
    private startX = 0;
    private scrollLeft = 0;

    // Minimum px of movement before we consider it a drag (avoids suppressing plain clicks)
    private readonly dragThreshold = 5;

    // Bound handler references for cleanup
    private onMouseDown: (e: MouseEvent) => void;
    private onMouseMove: (e: MouseEvent) => void;
    private onMouseUp: (e: MouseEvent) => void;
    private onMouseLeave: (e: MouseEvent) => void;
    private onClickCapture: (e: MouseEvent) => void;

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private ngZone: NgZone,
    ) {}

    ngOnInit(): void {
        this.el = this.elementRef.nativeElement;
        this.el.style.cursor = 'grab';

        // Build bound handlers
        this.onMouseDown = (e: MouseEvent) => this.handleMouseDown(e);
        this.onMouseMove = (e: MouseEvent) => this.handleMouseMove(e);
        this.onMouseUp = () => this.handleMouseUp();
        this.onMouseLeave = () => this.handleMouseUp();
        this.onClickCapture = (e: MouseEvent) => this.handleClickCapture(e);

        // Register outside Angular zone for performance (no change detection on every mousemove)
        this.ngZone.runOutsideAngular(() => {
            this.el.addEventListener('mousedown', this.onMouseDown);
            this.el.addEventListener('mousemove', this.onMouseMove);
            this.el.addEventListener('mouseup', this.onMouseUp);
            this.el.addEventListener('mouseleave', this.onMouseLeave);
            // Capture phase so we can intercept clicks before they reach child buttons
            this.el.addEventListener('click', this.onClickCapture, true);
        });
    }

    ngOnDestroy(): void {
        this.el.removeEventListener('mousedown', this.onMouseDown);
        this.el.removeEventListener('mousemove', this.onMouseMove);
        this.el.removeEventListener('mouseup', this.onMouseUp);
        this.el.removeEventListener('mouseleave', this.onMouseLeave);
        this.el.removeEventListener('click', this.onClickCapture, true);
    }

    // -------------------------------------------------------------------
    // Handlers
    // -------------------------------------------------------------------

    private handleMouseDown(e: MouseEvent): void {
        this.isDown = true;
        this.wasDragged = false;
        this.startX = e.pageX - this.el.offsetLeft;
        this.scrollLeft = this.el.scrollLeft;
        this.el.style.cursor = 'grabbing';
        this.el.style.userSelect = 'none';
    }

    private handleMouseMove(e: MouseEvent): void {
        if (!this.isDown) return;
        e.preventDefault();

        const x = e.pageX - this.el.offsetLeft;
        const walk = x - this.startX;

        if (Math.abs(walk) > this.dragThreshold) {
            this.wasDragged = true;
        }

        this.el.scrollLeft = this.scrollLeft - walk;
    }

    private handleMouseUp(): void {
        if (!this.isDown) return;
        this.isDown = false;
        this.el.style.cursor = 'grab';
        this.el.style.removeProperty('user-select');
    }

    /**
     * Intercept clicks in capture phase: if the user just finished dragging,
     * stop the event so that child buttons (category chips) do not fire their (click) handler.
     */
    private handleClickCapture(e: MouseEvent): void {
        if (this.wasDragged) {
            e.stopPropagation();
            e.preventDefault();
            this.wasDragged = false;
        }
    }
}
